import React, { useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { logger } from "@/lib/logger";

function todayStr() {
  const now = new Date();
  const off = now.getTimezoneOffset();
  return new Date(now.getTime() - off * 60000).toISOString().slice(0, 10);
}
function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}
function fmt(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function SummaryPage() {
  const toast = useToast();
  const [start, setStart] = useState(daysAgo(7));
  const [end, setEnd] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [count, setCount] = useState(0);

  const generate = async () => {
    if (!start || !end) return toast.show({ variant: "error", title: "Pick dates" });
    if (start > end) return toast.show({ variant: "error", title: "Invalid range", description: "Start date must be before end date." });
    setLoading(true);
    setSummary(null);
    try {
      const { data } = await axios.post(
        `${API}/summary`,
        { start_date: start, end_date: end },
        { withCredentials: true }
      );
      setSummary(data.summary);
      setCount(data.activity_count);
    } catch (err) {
      toast.show({
        variant: "error",
        title: "Summary failed",
        description: err.response?.data?.detail || "Try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const setRange = (days) => {
    setStart(daysAgo(days));
    setEnd(todayStr());
  };

  const handleShare = async () => {
    if (!summary) return;
    const message = `📊 Activity Summary\n\n📅 ${fmt(start)} → ${fmt(end)}\n📝 ${count} activities\n\n${summary}\n\n— AusumLog`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Activity Summary", text: message });
      } else {
        await navigator.clipboard.writeText(message);
        toast.show({ variant: "success", title: "Copied to clipboard" });
      }
    } catch (e) {
      // Share cancelled by user or API unavailable — non-fatal.
      logger.warn("share/copy failed:", e);
    }
  };

  return (
    <div className="px-5 pt-6 max-w-2xl mx-auto" data-testid="summary-page">
      <header className="mb-5">
        <h1 className="text-3xl font-extrabold tracking-tight">AI Summary</h1>
        <p className="text-slate-400 text-sm mt-1">Smart recap of your activities across any date range.</p>
      </header>

      {/* Quick ranges */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        {[
          { label: "7 days", n: 7 },
          { label: "14 days", n: 14 },
          { label: "30 days", n: 30 },
        ].map((r) => (
          <button
            key={r.n}
            onClick={() => setRange(r.n)}
            data-testid={`quick-range-${r.n}`}
            className="bg-slate-800/60 hover:bg-slate-800 border border-slate-700 rounded-xl py-3 text-sm font-medium text-slate-200 btn-press"
          >
            Last {r.label}
          </button>
        ))}
      </div>

      {/* Date pickers */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4 mb-5 space-y-3">
        <DateRow label="From" value={start} onChange={setStart} testId="summary-start-date" />
        <DateRow label="To" value={end} onChange={setEnd} testId="summary-end-date" />
      </div>

      <button
        onClick={generate}
        disabled={loading}
        data-testid="generate-summary-btn"
        className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 disabled:opacity-60 text-white font-semibold py-3.5 rounded-2xl btn-press flex items-center justify-center gap-2 shadow-xl shadow-violet-900/30"
      >
        {loading ? (
          <>
            <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full spin"></span>
            Generating…
          </>
        ) : (
          <>
            <i className="fas fa-wand-magic-sparkles"></i> Generate AI summary
          </>
        )}
      </button>

      {/* Result */}
      {summary && (
        <div className="mt-6 fade-in">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
              <i className="fas fa-file-lines"></i>
            </div>
            <h2 className="font-semibold text-slate-100">Summary</h2>
            <span className="ml-auto text-xs bg-emerald-500/10 text-emerald-300 px-2.5 py-1 rounded-full">
              {count} {count === 1 ? "activity" : "activities"}
            </span>
          </div>
          <div data-testid="summary-result" className="bg-slate-800/60 border border-slate-700 rounded-2xl p-5 leading-relaxed text-slate-200 whitespace-pre-wrap">
            {summary}
          </div>
          <button
            onClick={handleShare}
            data-testid="share-summary-btn"
            className="mt-4 w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-xl btn-press flex items-center justify-center gap-2"
          >
            <i className="fas fa-share-nodes"></i> Share summary
          </button>
        </div>
      )}

      {!summary && !loading && (
        <div className="text-center mt-10 fade-in">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-800 flex items-center justify-center mb-3">
            <i className="fas fa-chart-line text-2xl text-slate-600"></i>
          </div>
          <p className="text-slate-500 text-sm max-w-xs mx-auto">
            Select a date range and tap generate to get a clear, AI-powered recap of your work.
          </p>
        </div>
      )}
    </div>
  );
}

function DateRow({ label, value, onChange, testId }) {
  const shift = (n) => {
    const d = new Date(value + "T00:00:00");
    d.setDate(d.getDate() + n);
    const off = d.getTimezoneOffset();
    onChange(new Date(d.getTime() - off * 60000).toISOString().slice(0, 10));
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-400 text-sm w-12">{label}</span>
      <button onClick={() => shift(-1)} className="w-10 h-10 rounded-lg bg-slate-900/70 border border-slate-700 text-blue-400 hover:bg-slate-800" aria-label="Previous day">
        <i className="fas fa-chevron-left"></i>
      </button>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="flex-1 bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 outline-none focus:border-blue-500 mono text-sm"
      />
      <button onClick={() => shift(1)} className="w-10 h-10 rounded-lg bg-slate-900/70 border border-slate-700 text-blue-400 hover:bg-slate-800" aria-label="Next day">
        <i className="fas fa-chevron-right"></i>
      </button>
    </div>
  );
}
